import { useMemo, useRef, useState, useCallback, useLayoutEffect } from 'react';

/**
 * useChatDisplay
 * Shared hook to:
 *  - optionally filter messages (hide agent chain-of-thought / action chunks)
 *  - virtualize by limiting displayed count and offering incremental loading
 *  - maintain scroll position when prepending older messages
 *
 * @param {Array} messages - full message list
 * @param {boolean} showAllThoughts - if false, collapses consecutive assistant/action responses
 * @param {number} initialCount - initial number of latest messages to render
 */
export function useChatDisplay(messages, showAllThoughts = false, initialCount = 15) {
  const [displayedCount, setDisplayedCount] = useState(initialCount);
  const scrollRestoreRef = useRef(null); // stores {heightBefore, scrollTop}
  const containerRef = useRef(null);

  const processed = useMemo(() => {
    if (!Array.isArray(messages) || messages.length === 0) return [];
    if (showAllThoughts) return messages;
    // Collapse logic: keep user/system messages and last assistant in a run
    const out = [];
    for (let i = 0; i < messages.length; i++) {
      const m = messages[i];
      if (!m) continue;
      if (m.role === 'user' || m.role === 'system') {
        out.push(m);
        continue;
      }
      if (m.role === 'assistant') {
        const next = messages[i + 1];
        if (!next || next.role !== 'assistant') {
          out.push(m);
        } else {
          // skip intermediate assistant chunk
        }
      } else {
        out.push(m);
      }
    }
    return out;
  }, [messages, showAllThoughts]);

  const displayed = useMemo(() => processed.slice(-displayedCount), [processed, displayedCount]);

  const hasMore = processed.length > displayed.length;

  const loadMore = useCallback(() => {
    const el = containerRef.current;
    if (el) {
      scrollRestoreRef.current = { heightBefore: el.scrollHeight, scrollTop: el.scrollTop };
    }
    setDisplayedCount(c => Math.min(c + 15, processed.length));
  }, [processed.length]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (scrollRestoreRef.current) {
      const { heightBefore, scrollTop } = scrollRestoreRef.current;
      const delta = el.scrollHeight - heightBefore;
      el.scrollTop = scrollTop + delta;
      scrollRestoreRef.current = null;
    } else {
      // Auto-scroll to bottom if user was near bottom
      const nearBottom = el.scrollHeight - el.clientHeight - el.scrollTop < 160;
      if (nearBottom) {
        el.scrollTop = el.scrollHeight;
      }
    }
  }, [displayed]);

  return { containerRef, processed, displayed, hasMore, loadMore, displayedCount };
}

export default useChatDisplay;
